import { Box, Group, Stack, Text, Title } from '@mantine/core';
import { PortalButton } from 'components/Button/PortalButton';
import { useChangeStatusRule } from 'hooks/api/rule/useChangeStatusRule';
import { RuleItem, RuleStatuses, RuleTransitions } from 'models/rules';
import { Trans, useTranslation } from 'react-i18next';
import { BsCheck2, BsCheckCircle, BsPencil } from 'react-icons/bs';
import { CreateRuleFormProvider, useCreateRuleForm } from 'context/FormContext';
import classes from 'global.module.css';
import { formatDate } from 'utils/dates';
import { useDrawerContext } from 'context/hooks';
import { modals } from '@mantine/modals';
import { createConfirmResetRuleModal } from 'components/Modal/ConfirmModals';
import { useDefineRuleFormValidations } from './DefineRule/hooks/useDefineRuleFormValidations';
import { BasicInfoFields } from './DefineRule/Fields/BasicInfoFields';
import { RuleConfigurationFields } from './DefineRule/Fields/RuleConfigurationFields';
import { DefineConditionsFields } from './DefineRule/Fields/DefineConditionsFields';

interface Props {
  item: RuleItem;
}

export const ApprovedRuleForm = ({ item }: Props) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { setFormValues } = useDefineRuleFormValidations();
  const form = useCreateRuleForm({
    initialValues: setFormValues({ item, formDisabled: true }),
  });

  const { mutate: changeStatusRule } = useChangeStatusRule({
    reset: () => form.setFieldValue('formActionsDisabled', false),
  });

  const { values } = form;

  const handleEnableRule = () => {
    form.setFieldValue('formActionsDisabled', true);
    changeStatusRule({ item, transition: RuleTransitions.enable });
  };

  const handleResetRule = () => {
    form.setFieldValue('formActionsDisabled', true);
    changeStatusRule({ item, transition: RuleTransitions.reset });
  };

  return (
    <CreateRuleFormProvider form={form}>
      <Stack>
        <Box p={'md'} className={classes.approvalBox}>
          <Group align={'center'} gap={'xs'} c={'var(--mantine-color-turquoise-5'}>
            <BsCheckCircle size={20} />
            <Title order={3}>{t('rule_approval_label')}</Title>
          </Group>
          {item.approval && (
            <Text size={'sm'} c={'var(--mantine-primary-color-filled)'}>
              <Trans
                i18nKey={'rule_approval_text'}
                values={{
                  name: `${item.approval.userLastName} ${item.approval.userFirstName}`,
                  date: `${formatDate({ timestamp: new Date(item.approval.timestamp), formatWithTime: true, timeWithSeconds: false })}`,
                }}
              />
            </Text>
          )}
        </Box>

        <BasicInfoFields />
        <RuleConfigurationFields />
        <DefineConditionsFields />

        <Group mt={'xl'} justify={'flex-start'}>
          {item.status === RuleStatuses.approved && (
            <PortalButton
              label={t('enable_label')}
              Icon={BsCheck2}
              disabled={values.formActionsDisabled}
              onClick={handleEnableRule}
            />
          )}
          <PortalButton
            label={t('edit_label')}
            Icon={BsPencil}
            disabled={values.formActionsDisabled}
            variant={'light'}
            onClick={() => modals.openConfirmModal(createConfirmResetRuleModal(handleResetRule, item.status))}
          />
          <PortalButton
            variant={'outline'}
            onClick={() => closeDrawer()}
            label={t('cancel_label')}
            disabled={values.formActionsDisabled}
          />
        </Group>
      </Stack>
    </CreateRuleFormProvider>
  );
};
